'use client';

import React from 'react';
import { motion } from 'framer-motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';

const SECTION_ID = 'service-area-faq';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface FAQSectionProperties {
  faqs: { question: string; answer: string }[];
  townName: string;
  slug: string;
}

function FAQSection({ faqs, townName }: FAQSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
        <motion.div
          className={cls`flex flex-col items-center gap-10`}
          {...wrapperMotionProps}
        >
          <SectionHeader
            subtitle="FAQ"
            title="Frequently Asked Questions"
            description={`Common questions about building and renovating homes in ${townName}, Oklahoma.`}
          />

          <div className={cls`flex flex-col gap-6 w-full max-w-3xl mx-auto`}>
            {faqs.map((faq) => (
              <motion.div
                key={faq.question}
                className={cls`flex flex-col gap-3 p-6 rounded-lg border border-border bg-background`}
                {...itemMotionProps}
              >
                <TypographyH4 className="text-foreground">
                  {faq.question}
                </TypographyH4>
                <TypographySmall className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </TypographySmall>
              </motion.div>
            ))}
          </div>
        </motion.div>
    </SectionBase>
  );
}

export default FAQSection;
